import React from 'react';
import PropTypes from 'prop-types';
import * as yup from 'yup';
import Moment from 'moment';
import {alpha, useTheme} from '@mui/material/styles';
import Button from '@mui/material/Button';

import {FormattedMessage, useIntl} from 'react-intl';
import {validateDate} from '../../../utils/validationHelpers';
import {clampDateRangesToPolicy} from '../../../utils/dateHelpers';
import {fireGtmEvent} from '../../../utils/marketing.utils';
import {setDetailForm} from '../../../utils/persistOfferDetails';
import TripDatePicker from '../../../components/TripDatePicker';
import DestinationGrid from '../../../components/DestinationGrid';
import {publishDestinationChange} from '../../../components/destinationBackgrounds';
import {Card, Badge, Checkbox, Divider} from 'wobi-web-common/dist/v2/index.js';
import {scrollToFirstError} from '../../../utils/scrollToFirstError';
import TermsConsentLabel from 'wobi-web-common/dist/components/TermsConsentLabel';
import PrivacyNoticeLabel from 'wobi-web-common/dist/components/PrivacyNoticeLabel';
import FlowFooter from '../../../components/FlowFooter';
import './Tab1.css';

const Tab1Content = React.memo(
  ({
    idForAccessibility,
    className,
    submitCallback,
    validationCallback,
    formikSetField,
    submitButtonRef,
    formikValues,
    formikErrors,
    formikSetTouched,
    formikValidateForm,
    initialStartDateFromServer,
  }) => {
    // mocked policy start date, it has to be received from backend
    const [startDate, setStartDate] = React.useState('');
    const intl = useIntl();
    const [daysNumberMessage, setDaysNumberMessage] = React.useState('');
    const [endDate, setEndDate] = React.useState(null);
    const [isOtpFirst, setIsOtpFirst] = React.useState(false);
    const [hasAttemptedSubmit, setHasAttemptedSubmit] = React.useState(false);
    const theme = useTheme();

    React.useEffect(() => {
      setIsOtpFirst(sessionStorage.getItem('otpFirst'));
    }, []);

    if (
      !formikValues.policyStartDate &&
      startDate === '' &&
      initialStartDateFromServer
    ) {
      const today = new Moment();
      const date = Moment(initialStartDateFromServer, 'YYYY-MM-DD');
      const policyStartDate = date.isSame(today, 'day') ?
        date
          .set('hour', today.hour())
          .set('minutes', today.minute())
          .format('YYYY-MM-DDTHH:mm:ss') :
        Moment(date)
          .set('hour', 0)
          .set('minutes', 0)
          .format('YYYY-MM-DDTHH:mm:ss');
      formikSetField('policyStartDate', policyStartDate);
      setStartDate(policyStartDate);
    } else if (
      startDate === '' &&
      endDate === null &&
      formikValues.policyStartDate &&
      formikValues.policyEndDate
    ) {
      // update dates if insert after fill
      setEndDate(formikValues.policyEndDate);
      setStartDate(formikValues.policyStartDate);
      if (
        Moment(formikValues.policyStartDate).isValid() &&
        Moment(formikValues.policyEndDate).isValid()
      ) {
        const countDays =
          Number(
            Moment(formikValues.policyEndDate).diff(
              Moment(formikValues.policyStartDate),
              'days',
            ),
          ) + 1;
        countDays > 0 ?
          setDaysNumberMessage(
            intl.formatMessage({id: 'step1.tab1.days_num_message'}) +
                countDays,
          ) :
          setDaysNumberMessage('');
      } else {
        setDaysNumberMessage('');
      }
    }
    const startDayRecievedFromBackendMock = initialStartDateFromServer;
    const initialStartDate =
      formikValues.policyStartDate &&
        Moment(Moment(formikValues.policyStartDate).format('YYYY-MM-DD')) ||
      startDayRecievedFromBackendMock;
    const maxStartDate = Moment(startDayRecievedFromBackendMock).add(

      // one year max
      '1',
      'years',
    ).format('YYYY-MM-DDTHH:mm:ss');
    const maxEndDate = Moment(initialStartDate).add('180', 'days').format('YYYY-MM-DDTHH:mm:ss');
    const calendarMinYear = Moment(startDayRecievedFromBackendMock).year();
    const calendarMaxYear = calendarMinYear + 1;

    // Compute days count for display
    const daysCount = React.useMemo(() => {
      if (formikValues.policyStartDate && formikValues.policyEndDate &&
          Moment(formikValues.policyStartDate).isValid() && Moment(formikValues.policyEndDate).isValid()) {
        const count = Number(Moment(formikValues.policyEndDate).diff(Moment(formikValues.policyStartDate), 'days')) + 1;
        return count > 0 ? count : 0;
      }
      return 0;
    }, [formikValues.policyStartDate, formikValues.policyEndDate]);

    // dynamic callback to React.Component method
    const validationObject = {
      policyEndDate: yup
        .mixed()
        .required('שדה חובה')
        .test('underMax', 'זמן הביטוח עד 180 יום', function () {
          const policyEndDateMoment =
            this.parent.policyEndDate &&
              Moment(Moment(this.parent.policyEndDate).format('YYYY-MM-DD')) ||
            this.parent.policyStartDate &&
              Moment(
                Moment(this.parent.policyStartDate).format('YYYY-MM-DD'),
              ) ||
            startDayRecievedFromBackendMock;
          const initialStartDateMoment =
            this.parent.policyStartDate &&
              Moment(
                Moment(this.parent.policyStartDate).format('YYYY-MM-DD'),
              ) ||
            startDayRecievedFromBackendMock;
          const maxEndDate = Moment(initialStartDateMoment).add('180', 'days');
          return (
            // Allow same date as start
            // Strictly before the 181st day
            policyEndDateMoment.isBefore(maxEndDate) && policyEndDateMoment.isSameOrAfter(initialStartDateMoment)
          );
        }),
      policyStartDate: validateDate(
        startDayRecievedFromBackendMock,
        maxStartDate,
      ),
      targetCountries: yup.array().min(1, 'יש לבחור מדינה').required('שדה חובה'),
      isTerms: yup.boolean().oneOf([true], 'יש לאשר את תנאי השימוש'),
    };
    setTimeout(() => {
      validationCallback(validationObject);
    }, 1000);

    // Riders date ranges (e.g. extreme sport) were chosen for the old policy dates.
    // Correct them in the form itself so every screen shows the same dates that are sent.
    const updateTravelersDateRanges = (policyStartDate, policyEndDate, setFieldValue) => {
      if (!Array.isArray(formikValues.travelers)) {
        return;
      }
      setFieldValue('travelers', clampDateRangesToPolicy(formikValues.travelers, policyStartDate, policyEndDate));
    };

    const handleStartDatepickerChange = (value, setFieldValue) => {
      // if (value instanceof Date && !isNaN(value)) alert(value);
      const wasToday = Moment(formikValues.policyStartDate).isSame(
        Moment(),
        'day',
      );
      const isToday = Moment(value).isSame(Moment(), 'day');
      value = isToday ?
        Moment(value).format('YYYY-MM-DDTHH:mm:ss') :
        Moment(value)
          .set('hour', 0)
          .set('minutes', 0)
          .format('YYYY-MM-DDTHH:mm:ss');
      setFieldValue('policyStartDate', value);
      setStartDate(value);

      let policyEndDate = formikValues.policyEndDate;
      if (isToday != wasToday) {
        const updateEndDate = isToday ?
          Moment(endDate).format('YYYY-MM-DDTHH:mm:ss') :
          Moment(endDate)
            .set('hour', 0)
            .set('minutes', 0)
            .format('YYYY-MM-DDTHH:mm:ss');
        setFieldValue('policyEndDate', updateEndDate);
        setEndDate(updateEndDate);
        policyEndDate = updateEndDate;
      }
      updateTravelersDateRanges(value, policyEndDate, setFieldValue);

      if (Moment(endDate).isValid() && Moment(value).isValid()) {
        const countDays =
          Number(Moment(endDate).diff(Moment(value), 'days')) + 1;
        countDays > 0 ?
          setDaysNumberMessage(
            intl.formatMessage({id: 'step1.tab1.days_num_message'}) +
                countDays,
          ) :
          setDaysNumberMessage('');
      } else {
        setDaysNumberMessage('');
      }
    };

    const handleEndDatepickerChange = (value, setFieldValue) => {
      value = Moment(formikValues.policyStartDate).isSame(Moment(), 'day') ?
        Moment(value).format('YYYY-MM-DDTHH:mm:ss') :
        Moment(value)
          .set('hour', 0)
          .set('minutes', 0)
          .format('YYYY-MM-DDTHH:mm:ss');
      setFieldValue('policyEndDate', value);
      setEndDate(value);
      updateTravelersDateRanges(formikValues.policyStartDate, value, setFieldValue);

      if (Moment(startDate).isValid() && Moment(value).isValid()) {
        const countDays = Number(Moment(value).diff(startDate, 'days')) + 1;
        countDays > 0 ?
          setDaysNumberMessage(
            intl.formatMessage({id: 'step1.tab1.days_num_message'}) +
                countDays,
          ) :
          setDaysNumberMessage('');
      } else {
        setDaysNumberMessage('');
      }
    };

    const handleSubmit = async () => {
      setHasAttemptedSubmit(true);
      const errors = await formikValidateForm();
      if (Object.keys(errors).length === 0) {
        fireGtmEvent('confirmDestination', {
          dateDepart: formikValues.policyStartDate,
          dateReturn: formikValues.policyEndDate,
          destination: formikValues.targetCountries,
        });
        const {policyEndDate, targetFrame} = formikValues || {};
        if (!policyEndDate || !targetFrame || !targetFrame.length) {
          sessionStorage.removeItem('travel.details-form');
          Object.keys(validationObject).forEach((field) => {
            formikSetField(field, null);
            formikSetTouched(field, true);
          });
          window.rollbar.error(JSON.stringify({
            browser: window.navigator.appCodeName,
            browserVersion: window.navigator.appVersion,
            field: !policyEndDate ? 'policyEndDate' : 'targetFrame',
            fieldValue: formikValues.policyEndDate || formikValues.targetFrame,
          }));
        } else {
          setDetailForm({values: formikValues});
          submitCallback(1);
        }
      } else {
        Object.keys(validationObject).forEach((field) => formikSetTouched(field, true));
        scrollToFirstError(errors);
      }
    };

    return (
      <div
        className={`${className} tab1`}
        id={idForAccessibility}
        role='tabpanel'
        style={{
          '--travel-title-size': `clamp(${theme.typography.pxToRem(26)}, 3.2vw, ${theme.typography.pxToRem(34)})`,
          '--travel-title-weight': theme.typography.fontWeightBlack,
          '--travel-heading-size': theme.typography.pxToRem(19),
          '--travel-body-size': theme.typography.pxToRem(15),
          '--travel-small-size': theme.typography.pxToRem(13),
          '--travel-caption-size': theme.typography.pxToRem(12),
          '--travel-step-label-size': theme.typography.pxToRem(10),
          '--travel-error-size': theme.typography.pxToRem(13.5),
          '--travel-primary': theme.palette.primary.main,
          '--travel-primary-dark': theme.palette.primary.dark,
          '--travel-primary-contrast': theme.palette.primary.contrastText,
          '--travel-secondary': theme.palette.secondary.main,
          '--travel-text-secondary': theme.palette.text.secondary,
          '--travel-error': theme.palette.error.main,
          '--travel-primary-shadow': `0 12px 26px ${alpha(theme.palette.primary.main, 0.34)}`,
          '--travel-weight-bold': theme.typography.fontWeightBold,
          '--travel-weight-semibold': theme.typography.fontWeightSemiBold,
        }}
      >
        {/* Title */}
        <h1 className={`rd-page-title${isOtpFirst ? ' rd-page-title--returning' : ''}`}>
          {isOtpFirst
            ? intl.formatMessage({id: 'step1.tab1.welcomeAgain'})
            : intl.formatMessage({id: 'initialDetails.heading'})}
          <span className='rd-dot' aria-hidden='true' />
        </h1>
        <p className='rd-lead'>
          <FormattedMessage id='initialDetails.subtitle'/>
        </p>

        {/* Form Card */}
        <Card variant='floating' padding='lg' data-testid='destination-card' style={{
          marginTop: 22,
          width: '100%',
          maxWidth: 860,
          marginInline: 'auto',
          boxShadow: theme.shadows[4],
          padding: 'clamp(20px, 2.4vw, 30px)',
        }}>
          {/* Dates */}
          <div style={{maxWidth: 650, margin: '0 auto'}}>
            <TripDatePicker
              selectionMode='range'
              startValue={formikValues.policyStartDate}
              endValue={formikValues.policyEndDate}
              minDate={startDayRecievedFromBackendMock}
              maxStartDate={maxStartDate}
              maxEndDate={maxEndDate}
              minYear={calendarMinYear}
              maxYear={calendarMaxYear}
              onStartChange={(iso) => handleStartDatepickerChange(iso, formikSetField)}
              onEndChange={(iso) => {
                if (iso) {
                  handleEndDatepickerChange(iso, formikSetField);
                } else {
                  formikSetField('policyEndDate', null);
                  setEndDate(null);
                  setDaysNumberMessage('');
                }
              }}
              error={hasAttemptedSubmit && !!(formikErrors && (formikErrors.policyStartDate || formikErrors.policyEndDate))}
            />
          </div>

          {/* Duration badge */}
          {daysCount > 0 && (
            <div style={{textAlign: 'center', marginTop: 18}}>
              <Badge tone='neutral' style={{
                padding: '9px 18px',
                fontSize: theme.typography.pxToRem(14.5),
                fontWeight: theme.typography.fontWeightBold,
                color: theme.palette.secondary.dark,
                gap: 8,
              }}>
                <svg
                  width={17} height={17} viewBox='0 0 24 24' fill='none'
                  stroke='currentColor' strokeWidth={2}
                  strokeLinecap='round' strokeLinejoin='round'
                >
                  <path d='M8 2v4' />
                  <path d='M16 2v4' />
                  <path d='M3 10h18' />
                  <path d='M5 4h14a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z' />
                </svg>
                {intl.formatMessage({id: 'step1.tab1.days_num_message'})}{daysCount}
              </Badge>
            </div>
          )}

          {/* Date errors */}
          {hasAttemptedSubmit && formikErrors && (formikErrors.policyStartDate || formikErrors.policyEndDate) && (
            <div className='rd-error'>
              {formikErrors.policyStartDate || formikErrors.policyEndDate}
            </div>
          )}

          {/* Divider */}
          <Divider style={{margin: '24px 0 20px'}} />

          {/* Destinations */}
          <DestinationGrid
            value={formikValues.targetCountries}
            onSelect={(countries, continents, countriesHebrew) => {
              formikSetField('targetCountries', countries);
              formikSetField('targetFrame', continents);
              formikSetField('targetCountriesHebrew', countriesHebrew);
              publishDestinationChange(countries);
              setTimeout(() => {
                formikSetTouched('targetCountries', true);
              }, 0);
            }}
            error={hasAttemptedSubmit && !!(formikErrors && formikErrors.targetCountries)}
          />
        </Card>

        <div className='rd-consent'>
          <Checkbox
            checked={formikValues.isTerms}
            onChange={(event) => formikSetField('isTerms', event.target.checked)}
            data-testid='isTerms'
            data-validation-field='isTerms'
            invalid={hasAttemptedSubmit && Boolean(formikErrors?.isTerms)}
            style={{color: theme.palette.text.primary}}
            label={<TermsConsentLabel />}
          />
          {hasAttemptedSubmit && formikErrors?.isTerms && (
            <div className='rd-consent-error'>{formikErrors.isTerms}</div>
          )}
        </div>

        <FlowFooter
          showBackButton={false}
          onNext={handleSubmit}
          primaryButtonProps={{
            ref: submitButtonRef,
            'data-testid': 'continue-button-1',
            'aria-label': hasAttemptedSubmit && formikErrors && Object.keys(formikErrors).length
              ? `${intl.formatMessage({id: 'continue'})}, ${intl.formatMessage({id: 'validation.needToFillAllFields'})}`
              : null,
          }}
        />

        {/* Privacy */}
        <div className='rd-privacy'>
          <PrivacyNoticeLabel />
        </div>
      </div>
    );
  },
);

const Tab1 = React.memo((props) => {
  const intl = useIntl();
  const {
    className,
    idForAccessibility,
    initialStartDateFromServer,
    initialStartDateStatus,
    onRetryStartDate,
  } = props;

  if (initialStartDateStatus !== 'ready' || !initialStartDateFromServer) {
    const isLoading = initialStartDateStatus === 'loading';
    return (
      <div
        className={`${className || ''} tab1`}
        id={idForAccessibility}
        role='tabpanel'
        aria-busy={isLoading}
      >
        <h1 className='rd-page-title'>
          {intl.formatMessage({id: 'initialDetails.heading'})}
          <span className='rd-dot' aria-hidden='true' />
        </h1>
        {isLoading ? (
          <p role='status'>{intl.formatMessage({id: 'startDate.loading'})}</p>
        ) : (
          <div role='alert' data-testid='start-date-error'>
            <p>{intl.formatMessage({id: 'startDate.unavailable'})}</p>
            <Button
              color='primary'
              variant='contained'
              data-testid='retry-start-date'
              onClick={onRetryStartDate}
            >
              {intl.formatMessage({id: 'startDate.retry'})}
            </Button>
          </div>
        )}
      </div>
    );
  }

  return <Tab1Content {...props} />;
});

Tab1.propTypes = {
  className: PropTypes.string,
  formikSetField: PropTypes.func,
  formikSetTouched: PropTypes.func,
  formikValidateForm: PropTypes.func,
  formikValues: PropTypes.object,
  formikErrors: PropTypes.object,
  idForAccessibility: PropTypes.string,
  initialStartDateStatus: PropTypes.oneOf(['loading', 'ready', 'error']),
  initialStartDateFromServer: PropTypes.string,
  onRetryStartDate: PropTypes.func.isRequired,
  submitButtonRef: PropTypes.shape({current: PropTypes.any}),
  submitCallback: PropTypes.func,
  validationCallback: PropTypes.func,
};

export default Tab1;
